<script setup lang="ts">
import { TriangleAlert } from '@lucide/vue'
import { describeCron, type MailboxActionSchedule, SCHEDULE_PRESETS } from '@zerohuman/shared'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@zerohuman/ui'
import { computed, ref } from 'vue'

/**
 * How often a person's own inbox presses one of its buttons for them: by hand, or on one of the schedule presets, in the
 * enterprise's zone. Picking a cadence saves it at once; By hand stops it. A cadence set elsewhere (over MCP, or in
 * chat) that is no preset shows as itself, and stays until another is picked. A cadence whose last firing ran nothing
 * says so beside the picker, so a held cadence never looks like a running one.
 */
const props = defineProps<{
    label: string
    schedule: MailboxActionSchedule | null | undefined
    disabled?: boolean
    save: (cron: string) => Promise<unknown>
    clear: () => Promise<unknown>
}>()
const emit = defineEmits<{ changed: []; failed: [message: string] }>()

const BY_HAND = 'by-hand'
const saving = ref(false)
const current = computed(() => (props.schedule?.enabled ? props.schedule.cron : BY_HAND))
const custom = computed(() =>
    current.value !== BY_HAND && !SCHEDULE_PRESETS.some(preset => preset.cron === current.value) ? current.value : null,
)
const hint = computed(() => {
    const schedule = props.schedule
    if (!schedule?.enabled) {
        return `${props.label} runs only when you press it.`
    }
    const next = schedule.nextRunAt ? `Next ${new Date(schedule.nextRunAt).toLocaleString()}.` : ''
    const held = schedule.refusalReason
        ? ` The last time it was due it did not run: ${refusalText(schedule.refusalReason)}.`
        : ''
    return `${props.label} runs ${describeCron(schedule.cron).toLowerCase()} on your mailbox. ${next}${held}`
})

const held = computed(() =>
    props.schedule?.enabled && props.schedule.refusalReason
        ? `Did not run: ${refusalText(props.schedule.refusalReason)}`
        : null,
)

function refusalText(reason: string): string {
    switch (reason) {
        case 'previous_run_active':
            return 'the run before it was still going'
        case 'previous_run_failed':
            return 'the run before it failed, and is on Blockers'
        case 'not_a_mailbox_action':
            return 'the task no longer offers this button'
        case 'no_mail_address':
            return 'you have no mailbox here'
        default:
            return reason.replaceAll('_', ' ')
    }
}

async function pick(value: unknown): Promise<void> {
    if (typeof value !== 'string' || value === current.value || saving.value) {
        return
    }
    saving.value = true
    try {
        await (value === BY_HAND ? props.clear() : props.save(value))
        emit('changed')
    } catch (caught) {
        emit('failed', caught instanceof Error ? caught.message : `Could not change how often ${props.label} runs.`)
    } finally {
        saving.value = false
    }
}
</script>

<template>
	<Select :model-value="current" :disabled="disabled || saving" @update:model-value="pick">
		<SelectTrigger
			size="sm"
			class="h-8 w-auto gap-1 text-xs"
			:aria-label="`How often ${label} runs`"
			:title="hint"
			data-mailbox-action-schedule
		>
			<SelectValue />
		</SelectTrigger>
		<SelectContent>
			<SelectItem :value="BY_HAND">By hand</SelectItem>
			<SelectItem v-for="preset in SCHEDULE_PRESETS" :key="preset.cron" :value="preset.cron">{{ preset.label }}</SelectItem>
			<SelectItem v-if="custom" :value="custom">{{ describeCron(custom) }}</SelectItem>
		</SelectContent>
	</Select>
	<span
		v-if="held"
		class="inline-flex items-center gap-1 text-xs text-warning-amber"
		:title="hint"
		data-mailbox-action-schedule-held
	>
		<TriangleAlert class="size-3.5 shrink-0" aria-hidden="true" />
		{{ held }}
	</span>
</template>
